import * as React from 'react';
import { isInSoftBlockMode } from 'constants/geoBlocking';
import { NEW_LESSON_PAGE } from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import { GetCityClinicsQuery } from 'types/generated/server';
import { pluralize } from 'utils/shared/pluralize';
import { isLaunchCityMarket } from 'utils/shared/seo/isLaunchCityMarket';
import { useModal } from 'hooks/useModal';
import FilterIcon from 'svg/FilterIcon';
import Plus from 'svg/Plus';
import SafeAreaPage from 'layouts/SafeAreaPage';
import Breadcrumbs from 'components/Breadcrumbs';
import Button, { ButtonLink } from 'components/Button';
import CreateAccountToast from 'components/CreateAccountToast';
import DistanceDropdown from 'components/DiscoverPage/DistanceDropdown';
import FilterModal from 'components/DiscoverPage/FilterModal';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import MapToggleBar from 'components/Explore/MapToggleBar';
import GoogleMap from 'components/GoogleMap';
import TopNav from 'components/nav/TopNav';
import GeoLocationInitializer from 'components/utilities/GeoLocationInitializer';
import Head from 'components/utilities/Head';
import PopppinsFont from 'components/utilities/PoppinsFont';
import classNames from 'styles/utils/classNames';
import { SortDropdown } from './components';
import ClinicDatePicker from './components/ClinicDatePicker';
import TimeDropdown from './components/TimeDropdown';
import useClinics from './useClinics';

export interface ExploreClinicsProps {
  title?: string;
  description?: string;
  initialSport?: SportsEnum;
  cityData?: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    slug?: string;
    countrySubdivision?: {
      name?: string;
      slug?: string;
      country?: {
        id?: string;
        name?: string;
        slug?: string;
      };
    };
  };
  clinics?: GetCityClinicsQuery['lessons'];
  breadcrumbs?: {
    label: React.ReactNode;
    url: string;
    isActivePage?: boolean;
  }[];
  jsonLd?: string;
  noIndex?: boolean;
}

const ExploreClinics = ({
  title,
  description,
  initialSport,
  cityData,
  clinics,
  breadcrumbs,
  jsonLd,
  noIndex,
}: ExploreClinicsProps = {}) => {
  const cityCoordinates = cityData
    ? {
        latitude: cityData.latitude,
        longitude: cityData.longitude,
      }
    : undefined;

  const serverClinicData = clinics
    ? {
        clinics,
      }
    : undefined;

  const {
    handleToggleMap,
    setDistance,
    distance,
    ClinicsList,
    setSortBy,
    sortBy,
    clinicsMapProps,
    handleGoogleApiLoaded,
    isOpenMap,
    discoverContainerRef,
    isDiscoverSectionInView,
    selectedDate,
    setSelectedDate,
    ClinicDetailModal,
    handleClearAllFilters,
    clinicsLength,
    ClinicFilters,
    selectedTime,
    setSelectedTime,
    onAddressChange,
  } = useClinics({
    initialSport,
    initialCityCoordinates: cityCoordinates,
    serverClinicData,
  });

  const { closeModal, openModal, isOpen: isModalOpen } = useModal();

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;

  const finalTitle = title ?? 'Pickleball Clinics';
  const finalDescription =
    description ?? 'Discover pickleball clinics, group instruction, and skills training near you.';
  const shouldNoIndex = noIndex ?? (cityData ? !isLaunchCityMarket(cityData) : false);

  return (
    <>
      <GeoLocationInitializer>
        <SafeAreaPage isHideSidebar shouldHideTabBar={isOpenMap}>
          <Head
            canonicalType="currentUrlStripQuery"
            title={finalTitle}
            description={finalDescription}
            noIndex={shouldNoIndex}
          />
          <PopppinsFont />
          <div className="fixed -left-40 -top-40 -z-100">
            <h1>{finalTitle}</h1>
            {!!finalDescription && <p>{finalDescription}</p>}
          </div>
          <TopNav
            shouldHideNavigation={false}
            shouldShowMobile
            shouldLinkToAuthPage={false}
            shouldShowAdditionalLinks={false}
            topNavSearch={{
              isEnabled: true,
              isSecondRowOverlay: isOpenMap,
              isSecondRowScrollable: !isOpenMap,
              defaultExploreType: ExploreAllMarketPlacesTypesEnum.Clinics,
              defaultSport: initialSport,
              onAddressChange,
              instant: true,
            }}
          />
          <div
            ref={discoverContainerRef}
            className={classNames(
              'relative grid w-full grid-cols-[minmax(0,1fr)_45%]',
              hasBreadcrumbs ? 'grid-rows-[auto_auto_1fr]' : 'grid-rows-[auto_1fr]',
              isOpenMap
                ? hasBreadcrumbs
                  ? "[grid-template-areas:'header_header''extra-header_map''list_map'] max-lg:h-[calc(100vh-theme(spacing.topnav-mobile))] max-lg:grid-rows-[1fr] max-lg:[grid-template-areas:'map_map']"
                  : "[grid-template-areas:'header_header''list_map'] max-lg:h-[calc(100vh-theme(spacing.topnav-mobile))] max-lg:grid-rows-[1fr] max-lg:[grid-template-areas:'map_map']"
                : hasBreadcrumbs
                ? "h-full max-lg:[grid-template-areas:'header_header''extra-header_extra-header''list_list'] lg:min-h-[calc(100vh-theme(spacing.topnav-search))] lg:[grid-template-areas:'header_header''extra-header_map''list_map']"
                : "h-full max-lg:[grid-template-areas:'header_header''list_list'] lg:min-h-[calc(100vh-theme(spacing.topnav-search))] lg:[grid-template-areas:'header_header''list_map']",
            )}
          >
            <div
              className={classNames(
                'sticky  z-10 flex flex-col gap-4 border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary',
                'top-topnav-mobile [grid-area:header] lg:top-topnav-search',
                hasBreadcrumbs && 'border-b-0',
                isOpenMap && 'max-lg:hidden',
                !hasBreadcrumbs && 'sm:border-b',
              )}
            >
              <div className="flex justify-between gap-6 max-lg:flex-col max-lg:gap-ds-md lg:items-center">
                <div className="flex flex-1 items-center gap-3">
                  <h3 className="typography-product-heading-desktop max-lg:typography-product-heading-mobile shrink-0 whitespace-nowrap">
                    {pluralize({ count: clinicsLength, singular: 'Clinic' })} in this area
                  </h3>

                  <DistanceDropdown
                    distance={distance}
                    handleDistanceChange={(distance) => setDistance(distance)}
                    activeTabValue={ExploreAllMarketPlacesTypesEnum.Lessons}
                  />
                  <ClinicDatePicker
                    wrapperClassName={'basis-[8.75rem] max-md:hidden'}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                  />
                  <TimeDropdown
                    setTime={setSelectedTime}
                    time={selectedTime}
                    wrapperClassName={'max-md:w-full  hidden xl:block'}
                  />
                </div>

                <div className="flex items-center gap-8 max-lg:hidden">
                  <SortDropdown
                    wrapperClassName="max-xl:hidden"
                    setSortBy={setSortBy}
                    sortBy={sortBy}
                  />
                  <Button
                    onClick={() => openModal()}
                    variant="inverted"
                    className="w-fit shrink-0 whitespace-nowrap"
                    size="md"
                    iconLeft={
                      <FilterIcon className="h-4 w-4 text-color-bg-lightmode-invert dark:text-color-bg-darkmode-invert" />
                    }
                  >
                    More filters
                  </Button>
                </div>
              </div>
            </div>

            {hasBreadcrumbs && (
              <div
                className={classNames(
                  'p-4 pt-0 [grid-area:extra-header]',
                  isOpenMap && 'max-lg:hidden',
                )}
              >
                <Breadcrumbs breadcrumbs={breadcrumbs || []} />
              </div>
            )}

            {/* List section */}
            <div
              className={classNames(
                'flex min-w-0 flex-col overflow-auto pt-4',
                '[grid-area:list]',
                isOpenMap ? 'hidden lg:flex' : 'flex',
              )}
            >
              <div className="px-4 pb-4 lg:px-6">
                <ButtonLink
                  href={NEW_LESSON_PAGE}
                  variant="secondary"
                  size="md"
                  className="w-fit shrink-0 whitespace-nowrap"
                  iconLeft={<Plus className="h-4 w-4" />}
                >
                  List your clinic
                </ButtonLink>
              </div>
              {ClinicsList}
            </div>

            {/* Map section */}
            <div
              className={classNames(
                'sticky self-start [grid-area:map]',
                isOpenMap
                  ? 'block max-lg:h-full lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]'
                  : 'max-lg:hidden lg:top-[calc(theme(spacing.topnav-search)+77px)] lg:h-[calc(100vh-theme(spacing.topnav-search)-77px)]',
              )}
            >
              <GoogleMap
                {...clinicsMapProps}
                onGoogleApiLoaded={handleGoogleApiLoaded}
                softBlockPlaceholder={isInSoftBlockMode()}
              />
            </div>
          </div>

          <MapToggleBar
            isOpenMap={isOpenMap}
            onToggleMap={handleToggleMap}
            isInView={isDiscoverSectionInView}
            onOpenFilters={() => openModal()}
          />
        </SafeAreaPage>
        <FilterModal
          title={'Filters'}
          isOpen={isModalOpen}
          closeModal={closeModal}
          clearAll={handleClearAllFilters}
          closeButtonText={`Show ${clinicsLength} Clinics`}
        >
          {ClinicFilters}
        </FilterModal>
        {ClinicDetailModal}
        {/* Marketplace index page only — not the city SEO variants */}
        {!cityData && <CreateAccountToast />}
      </GeoLocationInitializer>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
};

export default ExploreClinics;
